/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import {
  ElementFactory,
  Question,
  Serializer,
  settings,
  QuestionCustomModel,
  type PanelModel,
  type SurveyModel,
  type ItemValue,
} from "survey-core";
import { type SurveyCreator } from "survey-creator-react";
import { ReactQuestionFactory } from "survey-react-ui";
import {
  CUSTOM_TYPE_PROFILE_SEARCH,
  NewProfileSearchQuestionProperties,
  ProfileSearchMultipleQuestionType,
  ProfileSearchMultipleQuestionProperties,
} from "shared/components/survey-js-form/questions/profile-search/profileSearchConstants";
import {
  type IRoleItemValue,
  type ProfileSearchQuestionData,
  type ProfileSearchQuestionFilterOption,
} from "shared/components/survey-js-form/questions/profile-search/ProfileSearchQuestionTypes";
import SurveyJSProfileSearch, {
  type SurveyJSProfileSearchProps,
} from "shared/components/survey-js-form/questions/profile-search/SurveyJSProfileSearch";
import { type SessionView } from "shared/mappers/database/session/session";
export type NewProfileSearchQuestionInputProps = {
  creator: SurveyCreator;
  isDisplayMode: boolean;
  question: Question;
  session: SessionView;
  baseUrl: string;
};
export class ProfileSearchQuestionModel extends Question {
  getType() {
    return CUSTOM_TYPE_PROFILE_SEARCH;
  }

  // You can add the filterOptions property here if you want typed access within the model
  // public get filterOptions(): Array<{ questionName: string, filterKey: string }> {
  //   const rawOptions = this.getPropertyValue("filterOptions") as ItemValue[];
  //   return (rawOptions || []).map(opt => ({
  //     questionName: String(opt.value),
  //     filterKey: String(opt.text)
  //   })).filter(opt => opt.questionName && opt.filterKey);
  // }
}

(settings as { customIcons: { [index: string]: string } }).customIcons[
  "icon-" + CUSTOM_TYPE_PROFILE_SEARCH
] = "icon-search";

const getAllowChangeProfilePermission = (question: Question): boolean => {
  const defaultPermission = true;

  const currentPanel = question.parent as PanelModel;
  if (!currentPanel) {
    return defaultPermission;
  }

  const containerQuestion = currentPanel.parentQuestion;

  const mainMultipleQuestion = containerQuestion?.parent;
  if (
    !(
      mainMultipleQuestion instanceof QuestionCustomModel &&
      mainMultipleQuestion.getType() === ProfileSearchMultipleQuestionType
    )
  ) {
    console.warn(
      "getAllowChangeProfilePermission: Expected parent structure not found.",
    );
    return defaultPermission;
  }

  const configuredRoles =
    (mainMultipleQuestion.getPropertyValue(
      ProfileSearchMultipleQuestionProperties.role,
    ) as IRoleItemValue[]) || [];

  const panelRoleValue = currentPanel.getQuestionByName("role")?.value;
  if (!panelRoleValue) {
    return defaultPermission;
  }

  const currentRoleConfig = configuredRoles.find(
    (roleConfig) => roleConfig.value === panelRoleValue,
  );
  if (!currentRoleConfig) {
    return defaultPermission;
  }

  return currentRoleConfig.allowChangeProfile !== false;
};

const getProfileSearchPanelIndex = (question: Question): number => {
  const panel = question.parent as PanelModel;
  return panel?.parentQuestion?.panels.indexOf(panel);
};

export const NewProfileSearchQuestion = (
  props: NewProfileSearchQuestionInputProps,
) => {
  props.question.errorLocation = "bottom";
  const existingValue = props.question.value;
  const newProfileWorkflowId = props.question.getPropertyValue(
    NewProfileSearchQuestionProperties.newProfileWorkflowId,
  );
  const directoryId = props.question.getPropertyValue(
    NewProfileSearchQuestionProperties.directoryId,
  );
  const emailRequired = props.question.getPropertyValue(
    NewProfileSearchQuestionProperties.emailRequired,
  );
  const titleLocation = props.question.getPropertyValue(
    NewProfileSearchQuestionProperties.titleLocation,
  );
  props.question.titleLocation = titleLocation || "hidden";
  const affiliationRequired = props.question.getPropertyValue(
    NewProfileSearchQuestionProperties.affiliationRequired,
  );
  const firstNameRequired = props.question.getPropertyValue(
    NewProfileSearchQuestionProperties.firstNameRequired,
  );
  const lastNameRequired = props.question.getPropertyValue(
    NewProfileSearchQuestionProperties.lastNameRequired,
  );
  const overrideAffiliation = props.question.getPropertyValue(
    NewProfileSearchQuestionProperties.overrideAffiliation,
  );
  const allowInlineParticipantEntry = props.question.getPropertyValue(
    NewProfileSearchQuestionProperties.allowInlineParticipantEntry,
  );

  const rawFilterOptions = props.question.getPropertyValue("filterOptions") as
    | ItemValue[]
    | undefined;

  const processedFilterOptions: ProfileSearchQuestionFilterOption[] = (
    rawFilterOptions || []
  )
    .map((opt) => ({
      questionName: String(opt.value), // 'value' from ItemValue is questionName
      filterKey: String(opt.text), // 'text' from ItemValue is filterKey
    }))
    .filter((opt) => opt.questionName && opt.filterKey); // Ensure both are present

  return SurveyJSProfileSearch({
    handleChanged: (val: { profile: ProfileSearchQuestionData }) => {
      props.question.value = val.profile;
    },
    directoryId,
    workflowId: newProfileWorkflowId,
    session: props.session,
    existingValue,
    emailRequired,
    affiliationRequired,
    firstNameRequired,
    lastNameRequired,
    overrideAffiliation,
    allowInlineParticipantEntry,
    allowChangeProfile: getAllowChangeProfilePermission(props.question),
    survey: props.question.survey as SurveyModel,
    filterOptions: processedFilterOptions,
    panelIndex: getProfileSearchPanelIndex(props.question),
  } as SurveyJSProfileSearchProps);
};

export const RegisterProfileSearchQuestionComponent = (
  session: SessionView,
  baseUrl?: string,
) => {
  ElementFactory.Instance.registerElement(
    CUSTOM_TYPE_PROFILE_SEARCH,
    (name) => {
      return new ProfileSearchQuestionModel(name);
    },
  );

  Serializer.addClass(
    CUSTOM_TYPE_PROFILE_SEARCH,
    [{ name: "height", default: "200px", category: "layout" }],
    function () {
      return new ProfileSearchQuestionModel("");
    },
    "question",
  );

  Serializer.addProperty(CUSTOM_TYPE_PROFILE_SEARCH, {
    name: "directoryId",
    displayName: "Choose a directory to search for profiles",
    type: "dropdown",
    isRequired: true,
    choices: [],
    category: "general",
  });

  Serializer.addProperty(CUSTOM_TYPE_PROFILE_SEARCH, {
    name: "newProfileWorkflowId",
    displayName: "Choose a workflow",
    type: "dropdown",
    isRequired: true,
    choices: [],
    category: "general",
  });

  Serializer.addProperty(CUSTOM_TYPE_PROFILE_SEARCH, {
    name: "overrideAffiliation",
    displayName: "Override Affiliation",
    type: "boolean",
    isRequired: false,
    default: false,
    category: "general",
  });

  Serializer.addProperty(CUSTOM_TYPE_PROFILE_SEARCH, {
    name: "allowInlineParticipantEntry",
    displayName:
      "Allow participants to be added in-line without creating profiles",
    type: "boolean",
    isRequired: false,
    default: false,
    category: "general",
  });

  Serializer.addProperty(CUSTOM_TYPE_PROFILE_SEARCH, {
    name: "emailRequired",
    displayName: "Email",
    type: "boolean",
    isRequired: false,
    default: false,
    category: "Create Profile Required Fields",
  });

  Serializer.addProperty(CUSTOM_TYPE_PROFILE_SEARCH, {
    name: "filterOptions",
    displayName: "Filter Options (questionName, filterKey)",
    type: "itemvalues",
    category: "general",
    default: [],
  });

  Serializer.addProperty(CUSTOM_TYPE_PROFILE_SEARCH, {
    name: "affiliationRequired",
    displayName: "Affiliation",
    type: "boolean",
    isRequired: false,
    default: false,
    category: "Create Profile Required Fields",
  });

  Serializer.addProperty(CUSTOM_TYPE_PROFILE_SEARCH, {
    name: "firstNameRequired",
    displayName: "First name",
    type: "boolean",
    isRequired: false,
    default: false,
    category: "Create Profile Required Fields",
  });

  Serializer.addProperty(CUSTOM_TYPE_PROFILE_SEARCH, {
    name: "lastNameRequired",
    displayName: "Last name",
    type: "boolean",
    isRequired: false,
    default: false,
    category: "Create Profile Required Fields",
  });

  Serializer.getProperty(CUSTOM_TYPE_PROFILE_SEARCH, "name").readOnly = true;

  ReactQuestionFactory.Instance.registerQuestion(
    CUSTOM_TYPE_PROFILE_SEARCH,
    (props: any) => {
      // generate key to update profile selects after panel move up/down (and prevent rerender when some fields are changed)

      const generateSurveyItemKey = () => {
        const { question } = props;
        if (!question) {
          console.error("SurveyJS: 'question' prop is missing.");
          return `error_no_question_${Math.random().toString(36).substring(2)}`;
        }

        const panel = question.data?.panel;
        const parentQuestion = question.parentQuestionValue;
        const baseId = panel?.id ?? question.id;

        const getFallbackKey = (reasonSuffix = "fallback", index = null) => {
          const indexPart = index !== null ? `_${index}` : "";

          if (baseId) {
            return `${baseId}-${reasonSuffix}${indexPart}`;
          }

          const randomPart = Math.random().toString(36).substring(2);
          return `${reasonSuffix}${indexPart}_${randomPart}`;
        };

        try {
          if (
            !panel ||
            !parentQuestion?.panels ||
            !Array.isArray(parentQuestion?.value)
          ) {
            let reason = "unknown_context";
            if (!panel) {
              reason = "missing_panel";
            } else if (!parentQuestion?.panels) {
              reason = "missing_parent_panels";
            } else if (!parentQuestion?.value) {
              reason = "missing_parent_value";
            } else {
              reason = "parent_value_not_array";
            }

            const contextId = panel?.id ?? question.id ?? "ctx";
            console.warn(
              `SurveyJS KeyGen: Missing context for key generation (${reason}). Panel: ${
                panel?.id
              }, Question: ${question.name ?? question.id}`,
            );
            return baseId
              ? `${baseId}-missingcontext_${reason}`
              : getFallbackKey(`missingcontext_${reason}`);
          }

          const {
            panels,
            value: logicalDataArray,
            name: parentName,
          } = parentQuestion;
          const panelId = panel.id;

          const structuralIndex = panels.indexOf(panel);

          if (structuralIndex < 0) {
            console.warn(
              `SurveyJS: Panel ${panelId} not found in parent PanelDynamic panels array (${parentName}).`,
            );
            return `${panelId}-notfoundinparentpanels`;
          }

          if (structuralIndex >= logicalDataArray.length) {
            console.warn(
              `SurveyJS: Data array length (${logicalDataArray.length}) mismatch for structuralIndex (${structuralIndex}) in parent ${parentName}.`,
            );
            return `${panelId}-datalengthmismatch_${structuralIndex}`;
          }

          const logicalItemData = logicalDataArray[structuralIndex];

          if (!logicalItemData) {
            console.warn(
              `SurveyJS: No logical data for structuralIndex ${structuralIndex} in parent ${parentName}.`,
            );
            return `${panelId}-nodata_${structuralIndex}`;
          }

          const profileIdInData = logicalItemData.profilesearch?.profileId;
          const profileSuffix = profileIdInData ?? `empty_${structuralIndex}`;

          return `${panelId}-${profileSuffix}`;
        } catch (error) {
          console.error("SurveyJS: Error during key generation:", error);
          return baseId ? `${baseId}-error` : getFallbackKey("error");
        }
      };

      return React.createElement(NewProfileSearchQuestion, {
        ...props,
        key: generateSurveyItemKey(),
        session,
        baseUrl,
      });
    },
  );
};
